import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FolderPicker } from "@/components/FolderPicker";
import { Send, Users, Search, X, Building2, Mail, Bookmark, Save, Trash2, Plus, Folder, ChevronRight, ChevronLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { n: 1, title: "Project & Trades" },
  { n: 2, title: "Vendors" },
  { n: 3, title: "Compose" },
  { n: 4, title: "Review & Send" },
];

export default function SendItb() {
  const location = useLocation();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState([]);
  const [csi, setCsi] = useState([]);
  const [subs, setSubs] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [csiCodes, setCsiCodes] = useState(location.state?.csi_codes || (location.state?.csi_code ? [location.state.csi_code] : []));
  const [csiSearch, setCsiSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("Invitation to Bid");
  const [body, setBody] = useState("You are invited to submit a bid for the referenced project. Please review the attached documents and reply to this email with your proposal (PDF).\n\nThank you.");
  const [sending, setSending] = useState(false);
  const [conn, setConn] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [msgTemplates, setMsgTemplates] = useState([]);
  const [msgSaveOpen, setMsgSaveOpen] = useState(false);
  const [msgTemplateName, setMsgTemplateName] = useState("");
  const [savingMsgTpl, setSavingMsgTpl] = useState(false);
  const [notifyMe, setNotifyMe] = useState("none");
  const [newProjOpen, setNewProjOpen] = useState(false);
  const [projPickerOpen, setProjPickerOpen] = useState(false);
  const [newProj, setNewProj] = useState({ name: "", code: "", box_folder_id: "", box_folder_name: "" });
  const [root, setRoot] = useState({ id: "", name: "" });
  const [savingProj, setSavingProj] = useState(false);
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [plansPickerOpen, setPlansPickerOpen] = useState(false);
  const [plansBusy, setPlansBusy] = useState(false);
  const [includePlansLink, setIncludePlansLink] = useState(true);

  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data));
  const loadTemplates = () => api.get("/templates").then((r) => setTemplates(r.data));
  const loadMsgTemplates = () => api.get("/message-templates").then((r) => setMsgTemplates(r.data));

  useEffect(() => {
    Promise.all([api.get("/projects"), api.get("/csi-codes"), api.get("/subcontractors"), api.get("/email/connection"), api.get("/templates"), api.get("/settings/routing"), api.get("/message-templates")])
      .then(([p, c, s, e, t, r, m]) => {
        setProjects(p.data); setCsi(c.data); setSubs(s.data);
        setConn(e.data?.connected ? e.data : null); setTemplates(t.data);
        setMsgTemplates(m.data);
        setRoot({ id: r.data.projects_root_folder_id || "0", name: r.data.projects_root_folder_name || "All Files" });
      });
  }, []);

  const saveNewProject = async () => {
    if (!newProj.name || !newProj.code) { toast.error("Name and code are required"); return; }
    setSavingProj(true);
    try {
      const { data } = await api.post("/projects", newProj);
      toast.success(`Project “${data.name}” created`);
      await loadProjects();
      setProjectId(data.id);
      setNewProjOpen(false);
      setNewProj({ name: "", code: "", box_folder_id: "", box_folder_name: "" });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingProj(false); }
  };

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    const tsubs = subs.filter((s) => t.subcontractor_ids.includes(s.id));
    const codes = [...new Set(tsubs.flatMap((s) => s.csi_codes || []))];
    setCsiCodes((prev) => [...new Set([...prev, ...codes])]);
    setSelected(t.subcontractor_ids);
    toast.success(`Applied bid list “${t.name}” (${t.subcontractor_ids.length} vendors)`);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) { toast.error("Name your bid list"); return; }
    if (selected.length === 0) { toast.error("Select some subcontractors first"); return; }
    try {
      await api.post("/templates", { name: templateName.trim(), subcontractor_ids: selected });
      toast.success("Bid list saved");
      setSaveOpen(false); setTemplateName("");
      loadTemplates();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deleteTemplate = async (tid) => { await api.delete(`/templates/${tid}`); loadTemplates(); toast.success("Bid list deleted"); };

  const applyMsgTemplate = (tid) => {
    const t = msgTemplates.find((x) => x.id === tid);
    if (!t) return;
    setSubject(t.subject); setBody(t.body);
    toast.success(`Loaded template “${t.name}”`);
  };

  const saveMsgTemplate = async () => {
    if (!msgTemplateName.trim()) { toast.error("Name your template"); return; }
    setSavingMsgTpl(true);
    try {
      await api.post("/message-templates", { name: msgTemplateName.trim(), subject, body });
      toast.success("Email template saved");
      setMsgSaveOpen(false); setMsgTemplateName("");
      loadMsgTemplates();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingMsgTpl(false); }
  };

  const deleteMsgTemplate = async (tid) => { await api.delete(`/message-templates/${tid}`); loadMsgTemplates(); toast.success("Template deleted"); };

  const toggleCode = (code) => setCsiCodes((cs) => cs.includes(code) ? cs.filter((x) => x !== code) : [...cs, code]);

  const filteredCsi = useMemo(() => {
    const q = csiSearch.trim().toLowerCase();
    return csi.filter((c) => !q || c.code.toLowerCase().includes(q) || c.title.toLowerCase().includes(q) || c.division.toLowerCase().includes(q));
  }, [csi, csiSearch]);

  const available = useMemo(() => {
    if (csiCodes.length === 0) return [];
    return subs.filter((s) => s.csi_codes?.some((c) => csiCodes.includes(c)));
  }, [subs, csiCodes]);

  useEffect(() => { setSelected((sel) => sel.filter((id) => available.some((s) => s.id === id))); }, [available]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = available.length > 0 && selected.length === available.length;
  const toggleAll = () => setSelected(allSelected ? [] : available.map((s) => s.id));

  const parseEmails = (v) => v.split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);

  const send = async () => {
    if (!projectId) { toast.error("Select a project"); return; }
    if (csiCodes.length === 0) { toast.error("Select at least one CSI code"); return; }
    if (selected.length === 0) { toast.error("Select at least one subcontractor"); return; }
    setSending(true);
    try {
      const cc = [...parseEmails(ccInput), ...(notifyMe === "cc" && user?.email ? [user.email] : [])];
      const bcc = [...parseEmails(bccInput), ...(notifyMe === "bcc" && user?.email ? [user.email] : [])];
      await api.post("/itbs", { project_id: projectId, csi_codes: csiCodes, subcontractor_ids: selected, subject, body, cc, bcc, include_plans_link: includePlansLink });
      toast.success(`ITB sent to ${selected.length} subcontractor(s)`);
      setSelected([]); setStep(1);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const attachPlansFolder = async (folder) => {
    setPlansPickerOpen(false);
    setPlansBusy(true);
    try {
      await api.post(`/projects/${projectId}/plans-link`, { folder_id: folder.id, folder_name: folder.name });
      await loadProjects();
      setIncludePlansLink(true);
      toast.success(`Plans folder linked: ${folder.name}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPlansBusy(false); }
  };

  const regeneratePlansLink = async () => {
    const p = projects.find((x) => x.id === projectId);
    if (!p?.plans_link_folder_id) return;
    setPlansBusy(true);
    try {
      await api.post(`/projects/${projectId}/plans-link`, { folder_id: p.plans_link_folder_id, folder_name: p.plans_link_folder_name });
      await loadProjects();
      toast.success("Plans link regenerated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPlansBusy(false); }
  };

  const removePlansLink = async () => {
    setPlansBusy(true);
    try {
      await api.delete(`/projects/${projectId}/plans-link`);
      await loadProjects();
      toast.success("Plans link removed");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPlansBusy(false); }
  };

  const project = projects.find((p) => p.id === projectId);
  const plansUrl = project?.plans_link_url;
  const previewBody = body + (includePlansLink && plansUrl ? `\n\n———\nProject plans (view & download): ${plansUrl}` : "");
  const fullSubject = project ? `[${project.code}] ${subject}` : subject;
  const fromLine = conn ? `${conn.from_name ? conn.from_name + " " : ""}<${conn.email}>` : "your connected inbox";
  const selectedSubs = available.filter((s) => selected.includes(s.id));

  const canNext = step === 1 ? (projectId && csiCodes.length > 0) : step === 2 ? selected.length > 0 : true;
  const next = () => {
    if (step === 1 && !projectId) { toast.error("Select a project"); return; }
    if (step === 1 && csiCodes.length === 0) { toast.error("Select at least one CSI code"); return; }
    if (step === 2 && selected.length === 0) { toast.error("Select at least one vendor"); return; }
    setStep((s) => Math.min(4, s + 1));
  };

  return (
    <>
      <PageHeader title="Send Invitation to Bid" description="A quick 4-step flow: pick the project, choose vendors, compose, and dispatch" />

      {/* Stepper */}
      <div className="px-8 pt-6">
        <div className="flex items-center gap-2 max-w-3xl">
          {STEPS.map((s, i) => {
            const done = step > s.n;
            const active = step === s.n;
            return (
              <div key={s.n} className="flex items-center gap-2 flex-1 last:flex-none">
                <button onClick={() => s.n < step && setStep(s.n)} disabled={s.n > step} data-testid={`itb-stepper-${s.n}`}
                  className={`flex items-center gap-2 ${s.n <= step ? "cursor-pointer" : "cursor-not-allowed"}`}>
                  <span className={`h-8 w-8 shrink-0 flex items-center justify-center rounded-sm text-sm font-display font-bold transition-colors ${done ? "bg-primary text-primary-foreground" : active ? "bg-foreground text-background" : "bg-secondary text-muted-foreground"}`}>
                    {done ? <CheckCircle2 className="h-4 w-4" /> : String(s.n).padStart(2, "0")}
                  </span>
                  <span className={`text-sm font-semibold hidden sm:inline ${active ? "text-foreground" : "text-muted-foreground"}`}>{s.title}</span>
                </button>
                {i < STEPS.length - 1 && <div className={`h-px flex-1 ${done ? "bg-primary" : "bg-border"}`} />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-8">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.2 }}>

            {/* STEP 1 — Project & Trades */}
            {step === 1 && (
              <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="itb-wizard-step-1">
                <Card className="p-6 border border-border rounded-sm">
                  <Label className="text-xs uppercase tracking-widest">Project</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Select value={projectId} onValueChange={setProjectId}>
                      <SelectTrigger className="flex-1" data-testid="itb-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}</SelectContent>
                    </Select>
                    <Button type="button" variant="outline" onClick={() => setNewProjOpen(true)} data-testid="new-project-inline-btn"><Plus className="h-4 w-4 mr-1" />New</Button>
                  </div>
                  {projects.length === 0 && <p className="text-xs text-muted-foreground mt-1">No projects yet — click “New” to create one.</p>}
                  {project && (
                    <div className="mt-4 p-3 rounded-sm bg-secondary text-sm">
                      <p className="font-semibold flex items-center gap-1.5"><Building2 className="h-4 w-4 text-primary" />{project.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Code <span className="font-mono">{project.code}</span> · replies matching this code auto-route to its folder.</p>
                    </div>
                  )}
                </Card>

                <Card className="p-6 border border-border rounded-sm">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-widest">CSI trade codes ({csiCodes.length})</Label>
                    {csiCodes.length > 0 && <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setCsiCodes([])} data-testid="clear-csi">Clear all</button>}
                  </div>
                  {csiCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {csiCodes.map((code) => (
                        <Badge key={code} variant="default" className="gap-1 cursor-pointer" onClick={() => toggleCode(code)} data-testid={`selected-csi-${code.replace(/ /g, '')}`}>{code} <X className="h-3 w-3" /></Badge>
                      ))}
                    </div>
                  )}
                  <div className="relative mt-2">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={csiSearch} onChange={(e) => setCsiSearch(e.target.value)} placeholder="Search CSI codes…" className="pl-9" data-testid="csi-search" />
                  </div>
                  <div className="mt-2 max-h-72 overflow-y-auto border border-border rounded-sm">
                    {filteredCsi.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">No codes match.</div>}
                    {filteredCsi.map((c) => (
                      <label key={c.code} className="flex items-center gap-3 px-3 py-2 border-b border-border hover:bg-accent cursor-pointer text-sm" data-testid={`csi-option-${c.code.replace(/ /g, '')}`}>
                        <Checkbox checked={csiCodes.includes(c.code)} onCheckedChange={() => toggleCode(c.code)} />
                        <span className="font-mono text-xs w-16 shrink-0">{c.code}</span>
                        <span className="flex-1 truncate">{c.title}</span>
                        <Badge variant="secondary" className="shrink-0">{c.subcontractor_count} subs</Badge>
                      </label>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {/* STEP 2 — Vendors */}
            {step === 2 && (
              <div className="max-w-4xl" data-testid="itb-wizard-step-2">
                <Card className="p-6 border border-border rounded-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /><h3 className="font-display font-bold text-lg">Choose vendors</h3><Badge variant="secondary">{selected.length}/{available.length} selected</Badge></div>
                    <div className="flex items-center gap-2">
                      {available.length > 0 && <button className="text-xs text-primary font-medium" onClick={toggleAll} data-testid="toggle-all-recipients">{allSelected ? "Deselect all" : "Select all"}</button>}
                      <Button size="sm" variant="ghost" onClick={() => setSaveOpen(true)} disabled={selected.length === 0} data-testid="save-bidlist-btn"><Save className="h-4 w-4 mr-1" />Save as bid list</Button>
                    </div>
                  </div>

                  {templates.length > 0 && (
                    <div className="mb-4 pb-4 border-b border-border">
                      <Label className="text-xs uppercase tracking-widest flex items-center gap-1 mb-2"><Bookmark className="h-3.5 w-3.5" />Load a saved bid list</Label>
                      <div className="flex flex-wrap gap-2">
                        {templates.map((t) => (
                          <div key={t.id} className="flex items-center gap-1 border border-border rounded-sm pl-3 pr-1 py-1 hover:border-primary transition-colors" data-testid={`template-${t.id}`}>
                            <button onClick={() => applyTemplate(t.id)} className="text-sm font-medium" data-testid={`apply-template-${t.id}`}>{t.name} <span className="text-muted-foreground">({t.subcontractor_ids.length})</span></button>
                            <button onClick={() => deleteTemplate(t.id)} className="p-1 text-muted-foreground hover:text-destructive" data-testid={`delete-template-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {csiCodes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Go back and select CSI codes to see matching subcontractors.</p>
                  ) : available.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No subcontractors tagged with the selected code(s).</p>
                  ) : (
                    <div className="border border-border rounded-sm divide-y divide-border max-h-[30rem] overflow-y-auto">
                      {available.map((s) => {
                        const matched = (s.csi_codes || []).filter((c) => csiCodes.includes(c));
                        const on = selected.includes(s.id);
                        return (
                          <label key={s.id} className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${on ? "bg-primary/5" : "hover:bg-accent"}`} data-testid={`itb-vendor-table-row`}>
                            <Checkbox checked={on} onCheckedChange={() => toggle(s.id)} className="mt-0.5" data-testid={`recipient-${s.id}`} />
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-sm flex items-center gap-1.5 truncate"><Building2 className="h-3.5 w-3.5 text-primary shrink-0" />{s.company_name || s.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{s.name}{s.name && s.email ? " · " : ""}{s.email}</p>
                            </div>
                            <div className="flex flex-wrap gap-1 justify-end max-w-[40%]">{matched.map((c) => <Badge key={c} variant="secondary" className="font-mono text-[10px] px-1.5 py-0">{c}</Badge>)}</div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </div>
            )}

            {/* STEP 3 — Compose */}
            {step === 3 && (
              <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="itb-wizard-step-3">
                <Card className="p-6 border border-border rounded-sm space-y-5">
                  <div className="flex flex-wrap items-end gap-2 pb-4 border-b border-border">
                    <div className="flex-1 min-w-[180px]">
                      <Label className="text-xs uppercase tracking-widest flex items-center gap-1"><Bookmark className="h-3.5 w-3.5" />Email template</Label>
                      <Select onValueChange={applyMsgTemplate}>
                        <SelectTrigger className="mt-1.5" data-testid="msg-template-select"><SelectValue placeholder={msgTemplates.length ? "Load a saved template…" : "No saved templates yet"} /></SelectTrigger>
                        <SelectContent>
                          {msgTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id} data-testid={`msg-template-option-${t.id}`}>
                              <span className="flex items-center justify-between gap-3 w-full">
                                <span>{t.name}</span>
                                <Trash2 role="button" tabIndex={0} onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); deleteMsgTemplate(t.id); }} className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" data-testid={`msg-template-delete-${t.id}`} />
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setMsgSaveOpen(true)} data-testid="save-msg-template-btn"><Save className="h-3.5 w-3.5 mr-1" />Save as template</Button>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Subject</Label>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1.5" data-testid="itb-subject" />
                    {project && <p className="text-xs text-muted-foreground mt-1">Sends as: <span className="font-mono">[{project.code}] {subject}</span></p>}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Message</Label>
                    <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="mt-1.5" data-testid="itb-body" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-widest">CC (optional)</Label><Input value={ccInput} onChange={(e) => setCcInput(e.target.value)} placeholder="a@x.com, b@y.com" className="mt-1.5" data-testid="itb-cc" /></div>
                    <div><Label className="text-xs uppercase tracking-widest">BCC (optional)</Label><Input value={bccInput} onChange={(e) => setBccInput(e.target.value)} placeholder="c@z.com" className="mt-1.5" data-testid="itb-bcc" /></div>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/50 border border-border rounded-sm px-3 py-2" data-testid="bcc-privacy-note">
                    <Users className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                    <span>Every subcontractor is <span className="font-medium text-foreground">BCC'd automatically</span> — recipients never see each other's addresses, bids, or replies.</span>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Notify me of replies</Label>
                    <div className="flex gap-2 mt-2">
                      {[["none", "Don't add me"], ["cc", "CC me"], ["bcc", "BCC me"]].map(([val, lbl]) => (
                        <button key={val} onClick={() => setNotifyMe(val)} data-testid={`notify-${val}`} className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${notifyMe === val ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-border pt-4">
                    <Label className="text-xs uppercase tracking-widest flex items-center gap-1"><Folder className="h-3.5 w-3.5" />Plans folder link</Label>
                    <p className="text-xs text-muted-foreground mt-1 mb-2">Attach a view &amp; download link to a Box folder of plans/specs so subs can review before bidding.</p>
                    {plansUrl ? (
                      <div className="space-y-2">
                        <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="include-plans-link-label">
                          <Checkbox checked={includePlansLink} onCheckedChange={(v) => setIncludePlansLink(!!v)} className="mt-0.5" data-testid="include-plans-link" />
                          <span>Include plans link for <span className="font-medium">{project?.plans_link_folder_name || "folder"}</span> in this ITB</span>
                        </label>
                        <div className="flex items-center gap-2 text-xs">
                          <a href={plansUrl} target="_blank" rel="noreferrer" className="text-primary underline truncate max-w-[16rem]" data-testid="plans-link-url">{plansUrl}</a>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => setPlansPickerOpen(true)} disabled={plansBusy} data-testid="change-plans-folder-btn">Change folder</Button>
                          <Button size="sm" variant="outline" onClick={regeneratePlansLink} disabled={plansBusy} data-testid="regenerate-plans-link-btn">Regenerate</Button>
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={removePlansLink} disabled={plansBusy} data-testid="remove-plans-link-btn"><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</Button>
                        </div>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setPlansPickerOpen(true)} disabled={plansBusy || !projectId} data-testid="pick-plans-folder-btn"><Folder className="h-3.5 w-3.5 mr-1" />{plansBusy ? "Working…" : "Pick plans folder"}</Button>
                    )}
                  </div>
                </Card>

                <div className="lg:sticky lg:top-8">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Live preview</Label>
                  <div className="rounded-sm border border-border overflow-hidden shadow-sm">
                    <div className="bg-secondary px-4 py-3 space-y-1.5 border-b border-border">
                      <div className="flex gap-2 text-sm"><span className="w-16 text-muted-foreground shrink-0">From</span><span className="font-medium flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-primary" />{fromLine}</span></div>
                      <div className="flex gap-2 text-sm"><span className="w-16 text-muted-foreground shrink-0">To</span><span className="font-medium flex items-center gap-1">{conn?.email || fromLine}</span></div>
                      <div className="flex gap-2 text-sm"><span className="w-16 text-muted-foreground shrink-0">Bcc</span><span className="flex-1">{selectedSubs.length === 0 ? <span className="text-muted-foreground">No recipients</span> : <span className="flex flex-wrap items-center gap-1"><Badge variant="secondary">{selectedSubs.length} vendor{selectedSubs.length === 1 ? "" : "s"}</Badge><span className="text-xs text-muted-foreground">(hidden from each other)</span></span>}</span></div>
                      <div className="flex gap-2 text-sm"><span className="w-16 text-muted-foreground shrink-0">Subject</span><span className="font-semibold">{fullSubject}</span></div>
                    </div>
                    <div className="p-5 bg-card text-sm whitespace-pre-wrap leading-relaxed min-h-[200px]" data-testid="itb-preview-body">{previewBody}</div>
                  </div>
                  {!conn && <p className="text-xs text-amber-600 mt-2">No inbox connected — connect one in Settings before sending.</p>}
                </div>
              </div>
            )}

            {/* STEP 4 — Review & Dispatch */}
            {step === 4 && (
              <div className="max-w-2xl" data-testid="itb-wizard-step-4">
                <Card className="p-8 border border-border rounded-sm">
                  <h3 className="font-display font-black text-3xl tracking-tighter mb-6">Ready to dispatch</h3>
                  <dl className="space-y-4">
                    <div className="flex items-baseline justify-between border-b border-border pb-3"><dt className="text-xs uppercase tracking-widest text-muted-foreground">Project</dt><dd className="font-semibold text-right">{project ? `${project.name} · ${project.code}` : "—"}</dd></div>
                    <div className="flex items-baseline justify-between border-b border-border pb-3"><dt className="text-xs uppercase tracking-widest text-muted-foreground">Trades</dt><dd className="font-mono text-right text-sm">{csiCodes.join(", ") || "—"}</dd></div>
                    <div className="flex items-baseline justify-between border-b border-border pb-3"><dt className="text-xs uppercase tracking-widest text-muted-foreground">Recipients</dt><dd className="font-display font-black text-4xl text-primary">{selected.length}</dd></div>
                    <div className="flex items-baseline justify-between border-b border-border pb-3"><dt className="text-xs uppercase tracking-widest text-muted-foreground">Subject</dt><dd className="font-semibold text-right">{fullSubject}</dd></div>
                    {(notifyMe !== "none" || ccInput || bccInput) && <div className="flex items-baseline justify-between border-b border-border pb-3"><dt className="text-xs uppercase tracking-widest text-muted-foreground">Copies</dt><dd className="text-sm text-right">{notifyMe !== "none" ? `${notifyMe.toUpperCase()} me` : ""}{ccInput ? ` · CC: ${ccInput}` : ""}{bccInput ? ` · BCC: ${bccInput}` : ""}</dd></div>}
                    {includePlansLink && plansUrl && <div className="flex items-baseline justify-between border-b border-border pb-3"><dt className="text-xs uppercase tracking-widest text-muted-foreground">Plans link</dt><dd className="text-sm text-right" data-testid="review-plans-link">{project?.plans_link_folder_name || "Folder"} (view &amp; download)</dd></div>}
                  </dl>
                  {!conn && <p className="text-sm text-amber-600 mt-4">⚠ No inbox connected — connect one in Settings before sending.</p>}
                  <Button className="w-full mt-8 h-12 text-base" onClick={send} disabled={sending || !conn} data-testid="itb-dispatch-submit-button">
                    <Send className="h-5 w-5 mr-2" />{sending ? "Dispatching…" : `Dispatch ITB to ${selected.length} vendor(s)`}
                  </Button>
                </Card>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Wizard nav */}
        <div className="flex items-center justify-between mt-8 max-w-4xl">
          <Button variant="outline" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="itb-back-btn"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
          {step < 4 && <Button onClick={next} disabled={!canNext} data-testid="itb-next-btn">Continue<ChevronRight className="h-4 w-4 ml-1" /></Button>}
        </div>
      </div>

      {/* Save bid list dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-bidlist-dialog">
          <DialogHeader><DialogTitle>Save bid list</DialogTitle><DialogDescription>Save the {selected.length} selected vendor(s) as a reusable list.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-widest">List name</Label><Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} className="mt-1.5" placeholder="e.g. Preferred Electrical" data-testid="bidlist-name" /></div>
            <Button onClick={saveTemplate} className="w-full" data-testid="save-bidlist-confirm">Save bid list</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save email template dialog */}
      <Dialog open={msgSaveOpen} onOpenChange={setMsgSaveOpen}>
        <DialogContent data-testid="save-msg-template-dialog">
          <DialogHeader><DialogTitle>Save email template</DialogTitle><DialogDescription>Save the current subject &amp; message so you can reuse it on future ITBs.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-widest">Template name</Label><Input value={msgTemplateName} onChange={(e) => setMsgTemplateName(e.target.value)} className="mt-1.5" placeholder="e.g. Standard ITB" data-testid="msg-template-name" /></div>
            <div className="text-xs text-muted-foreground bg-secondary/50 border border-border rounded-sm px-3 py-2">
              <p className="font-medium text-foreground mb-1">Subject</p><p className="truncate">{subject || "—"}</p>
            </div>
            <Button onClick={saveMsgTemplate} disabled={savingMsgTpl} className="w-full" data-testid="save-msg-template-confirm">{savingMsgTpl ? "Saving…" : "Save template"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New project dialog */}
      <Dialog open={newProjOpen} onOpenChange={setNewProjOpen}>
        <DialogContent data-testid="new-project-dialog">
          <DialogHeader><DialogTitle>New project</DialogTitle><DialogDescription>Create a project on the fly. Optionally pick its storage folder so replies route automatically.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-widest">Project name</Label><Input value={newProj.name} onChange={(e) => setNewProj({ ...newProj, name: e.target.value })} className="mt-1.5" placeholder="Industrial Till 451" data-testid="np-name" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Project code</Label><Input value={newProj.code} onChange={(e) => setNewProj({ ...newProj, code: e.target.value })} className="mt-1.5" placeholder="2607-009" data-testid="np-code" /><p className="text-xs text-muted-foreground mt-1">Used in the ITB subject and to match subcontractor replies.</p></div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Storage folder {conn ? "" : "(connect storage to enable)"}</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 text-sm border border-border rounded-sm px-3 py-2 truncate">{newProj.box_folder_name || <span className="text-muted-foreground">No folder — you can map it later</span>}</div>
                <Button type="button" variant="outline" onClick={() => setProjPickerOpen(true)} data-testid="np-browse-btn"><Folder className="h-4 w-4 mr-1" />Browse / create</Button>
              </div>
            </div>
            <Button onClick={saveNewProject} disabled={savingProj} className="w-full" data-testid="np-save-btn">{savingProj ? "Creating…" : "Create project"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <FolderPicker open={projPickerOpen} onOpenChange={setProjPickerOpen} startFolderId={root.id || "0"} startName={root.name || "All Files"} onPick={(f) => { setNewProj({ ...newProj, box_folder_id: f.id, box_folder_name: f.name }); setProjPickerOpen(false); }} />
      <FolderPicker open={plansPickerOpen} onOpenChange={setPlansPickerOpen} startFolderId={project?.box_folder_id || "0"} startName={project?.box_folder_name || "All Files"} onPick={attachPlansFolder} />
    </>
  );
}
